/**
 * Collection management dialogs.
 *
 * Related modal dialogs for user-created collections:
 *   - ShareCollectionDialog: manage who a collection is shared with (members).
 *   - RenameCollectionDialog: overwrite a collection's display name.
 *   - DeleteCollectionDialog: confirm and perform deletion of a collection.
 *
 * They are colocated because they are a matched pair surfaced together from the
 * collection page's action bar and share the same domain concepts.
 */

import { useEffect, useMemo, useRef, useState } from "react"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Loader2 } from "lucide-react"
import { Input } from "@/components/ui/input"
import MultiSelectCombobox, { ComboboxOption } from "@/components/shared/MultiSelectCombobox"
import LoadingSpinner from "@/components/shared/LoadingSpinner"
import {
  BiblioCollection,
  updateCollectionMembers,
  updateCollectionName,
  deleteCollection,
} from "@/services/biblioService"
import { getPermittedUsersOptions, UserOption } from "@/services/datasetsService"
import { useAuth } from "@/hooks/useAuth"

/** The outcome of a successful save, passed back to the parent. */
export interface ShareResult {
  /** The affected collection id. */
  collectionId: number
  /**
   * True when emptying the members deleted the collection (user collection,
   * last member removed). The parent should navigate away from it.
   */
  deleted: boolean
  /** The updated collection record when not deleted; undefined when deleted. */
  updated?: BiblioCollection
  /**
   * True when the acting user removed their own access (and the collection was
   * not deleted). The parent should navigate away since the collection is no
   * longer visible to this user.
   */
  lostOwnAccess: boolean
}

/** Props for the ShareCollectionDialog component. */
interface ShareCollectionDialogProps {
  /**
   * The collection whose membership is being managed. When null, the dialog is
   * closed; passing a collection opens the dialog for that collection.
   */
  collection: BiblioCollection | null
  /** Called to request closing the dialog (e.g. Cancel, overlay, or Escape). */
  onClose: () => void
  /** Called after a successful save with a description of what changed. */
  onChanged: (result: ShareResult) => void
}

/**
 * Modal dialog that edits a collection's member (access) list.
 *
 * @param collection - The collection to manage, or null when closed.
 * @param onClose - Handler to close the dialog.
 * @param onChanged - Handler invoked with the save outcome on success.
 * @returns The rendered dialog.
 */
export default function ShareCollectionDialog({
  collection,
  onClose,
  onChanged,
}: ShareCollectionDialogProps): React.JSX.Element {
  const { getAccessToken, user } = useAuth()

  /**
   * Ref to the dialog's content element. The user picker's dropdown is portaled
   * into this element so the modal dialog does not block interaction with it.
   */
  const dialogContentRef = useRef<HTMLDivElement | null>(null)

  /** The current user's Auth0 "sub", used to detect self-removal. */
  const currentUserSub: string = user?.sub ?? ""

  /** All selectable users, loaded from the users endpoint. */
  const [users, setUsers] = useState<UserOption[]>([])
  /** Whether the users list is currently being fetched. */
  const [usersLoading, setUsersLoading] = useState<boolean>(true)
  /** Error message shown when the users list fails to load. */
  const [usersError, setUsersError] = useState<string | null>(null)

  /** The full set of member subs currently selected in the picker. */
  const [selectedSubs, setSelectedSubs] = useState<string[]>([])
  /** Whether a save request is in flight. */
  const [saving, setSaving] = useState<boolean>(false)
  /** Error message shown when the save request fails. */
  const [saveError, setSaveError] = useState<string | null>(null)
  /**
   * Whether the destructive empty-members confirmation is being shown. Set when
   * the user tries to save with no members remaining.
   */
  const [confirmingEmpty, setConfirmingEmpty] = useState<boolean>(false)

  const isOpen: boolean = collection !== null

  // Load the full users list whenever the dialog opens. The service caches the
  // result in memory, so re-opening the dialog does not re-hit the network.
  useEffect(() => {
    if (!isOpen) {
      return
    }

    let cancelled: boolean = false

    const loadUsers = async (): Promise<void> => {
      setUsersLoading(true)
      setUsersError(null)
      try {
        const token: string = await getAccessToken()
        const fetchedUsers: UserOption[] = await getPermittedUsersOptions(token)
        if (!cancelled) {
          setUsers(fetchedUsers)
        }
      } catch (err) {
        if (!cancelled) {
          const message: string =
            err instanceof Error ? err.message : "Failed to load users"
          setUsersError(message)
        }
      } finally {
        if (!cancelled) {
          setUsersLoading(false)
        }
      }
    }

    loadUsers()

    return () => {
      cancelled = true
    }
  }, [isOpen, getAccessToken])

  // Seed the selection from the collection each time the dialog opens for a
  // (potentially different) collection, and reset transient state.
  useEffect(() => {
    if (collection === null) {
      return
    }
    setSelectedSubs(collection.members ?? [])
    setSaveError(null)
    setConfirmingEmpty(false)
  }, [collection])

  /**
   * Picker options derived from the loaded users. Any member sub that is not in
   * the loaded users list (e.g. a user who has since been removed from Auth0)
   * is still shown as an option so it can be seen and removed, labelled by its
   * sub as a fallback.
   */
  const options: ComboboxOption[] = useMemo<ComboboxOption[]>(() => {
    const bySub = new Map<string, string>(users.map((u) => [u.sub, u.name]))
    const knownOptions: ComboboxOption[] = users.map((u) => ({
      value: u.sub,
      label: u.name,
    }))
    // Include any currently-selected sub that is not in the loaded user list so
    // it remains visible and removable rather than silently dropping out.
    const unknownSelected: ComboboxOption[] = selectedSubs
      .filter((sub) => !bySub.has(sub))
      .map((sub) => ({
        value: sub,
        label: sub === currentUserSub ? "You (removed from directory)" : sub,
      }))
    return [...knownOptions, ...unknownSelected]
  }, [users, selectedSubs, currentUserSub])

  /** Whether saving now would remove the current user's own access. */
  const willLoseOwnAccess: boolean =
    currentUserSub.length > 0 && !selectedSubs.includes(currentUserSub)

  /** Whether saving now would empty the collection (and thus delete it). */
  const willEmpty: boolean = selectedSubs.length === 0

  /**
   * Perform the save against the backend with the current selection, then
   * report the outcome to the parent and close.
   *
   * @returns A promise that resolves once the save attempt has completed.
   */
  const performSave = async (): Promise<void> => {
    if (collection === null || saving) {
      return
    }

    setSaving(true)
    setSaveError(null)

    try {
      const token: string = await getAccessToken()
      const response = await updateCollectionMembers({
        idToken: token,
        collectionId: collection.id,
        members: selectedSubs,
      })

      const deleted: boolean = response.deleted === true
      onChanged({
        collectionId: collection.id,
        deleted,
        updated: response.item,
        // A delete already implies loss of access; only report lostOwnAccess
        // separately for the non-deleted case so the parent can navigate away.
        lostOwnAccess: !deleted && willLoseOwnAccess,
      })
      onClose()
    } catch (err) {
      const message: string =
        err instanceof Error ? err.message : "Failed to update sharing"
      setSaveError(message)
      setConfirmingEmpty(false)
    } finally {
      setSaving(false)
    }
  }

  /**
   * Handle the primary action. When the save would empty the collection, first
   * require an explicit confirmation (since that deletes the collection);
   * otherwise save immediately.
   *
   * @returns A promise that resolves once the action has been handled.
   */
  const handlePrimaryAction = async (): Promise<void> => {
    if (willEmpty && !confirmingEmpty) {
      setConfirmingEmpty(true)
      return
    }
    await performSave()
  }

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        // Prevent closing (via overlay/escape) while a save is in flight so the
        // user cannot dismiss the dialog mid-request.
        if (!open && !saving) {
          onClose()
        }
      }}
    >
      <DialogContent className="sm:max-w-md" ref={dialogContentRef}>
        <DialogHeader>
          <DialogTitle>Share Collection</DialogTitle>
          <DialogDescription>
            {collection !== null
              ? `Manage who can access "${collection.collectionName}".`
              : "Manage who can access this collection."}
          </DialogDescription>
        </DialogHeader>

        {usersLoading ? (
          <LoadingSpinner variant="default" loadingContent="Loading users..." />
        ) : usersError !== null ? (
          <p className="text-xs text-destructive" role="alert">
            {usersError}
          </p>
        ) : (
          <div className="space-y-2">
            <MultiSelectCombobox
              options={options}
              selected={selectedSubs}
              onChange={(next) => {
                setSelectedSubs(next)
                // Any edit invalidates a pending empty-delete confirmation.
                setConfirmingEmpty(false)
              }}
              placeholder="Add or remove users…"
              id="share-collection-users"
              disabled={saving}
              listClassName="max-h-60"
              // Cap the selected-chips area so a large member list scrolls
              // within a fixed height rather than pushing the dialog taller.
              chipsClassName="max-h-24 overflow-y-auto items-start content-start"
              portalContainer={dialogContentRef}
            />

            {/* Warn when the user is about to remove their own access. */}
            {willLoseOwnAccess && !willEmpty && (
              <p className="text-xs text-amber-600 dark:text-amber-500">
                You are removing your own access. You will no longer see this
                collection after saving.
              </p>
            )}

            {/* Destructive consequence of emptying the member list. */}
            {willEmpty && (
              <p className="text-xs text-destructive">
                Removing the last member deletes this collection for everyone.
                This cannot be undone.
              </p>
            )}
          </div>
        )}

        {/* Inline error: shown only when a save attempt failed. */}
        {saveError !== null && (
          <p className="text-xs text-destructive" role="alert">
            {saveError}
          </p>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant={willEmpty ? "destructive" : "default"}
            onClick={handlePrimaryAction}
            disabled={saving || usersLoading || usersError !== null}
          >
            {saving && <Loader2 className="size-3.5 animate-spin" />}
            {saving
              ? "Saving..."
              : willEmpty
                ? confirmingEmpty
                  ? "Confirm delete"
                  : "Delete collection"
                : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/** Props for the DeleteCollectionDialog component. */
interface DeleteCollectionDialogProps {
  /**
   * The collection to delete. When null, the dialog is closed; passing a
   * collection opens the confirmation for that collection.
   */
  collection: BiblioCollection | null
  /** Called to request closing the dialog (e.g. Cancel, overlay, or Escape). */
  onClose: () => void
  /**
   * Called after the collection has been successfully deleted. The parent
   * should navigate away from the now-removed collection and refresh its list.
   *
   * @param collectionId - The id of the collection that was deleted.
   */
  onDeleted: (collectionId: number) => void | Promise<void>
}

/**
 * Confirmation dialog that deletes a collection.
 *
 * Deleting a collection also unlinks it from its sources on the backend. This
 * is destructive and cannot be undone, so the action is gated behind an
 * explicit confirmation. The dialog owns the delete request lifecycle and
 * surfaces failures inline.
 *
 * @param props - Component props.
 * @param props.collection - The collection to delete, or null when closed.
 * @param props.onClose - Handler to close the dialog.
 * @param props.onDeleted - Handler invoked with the deleted collection id on
 *   success.
 * @returns The rendered confirmation dialog.
 */
export function DeleteCollectionDialog({
  collection,
  onClose,
  onDeleted,
}: DeleteCollectionDialogProps): React.JSX.Element {
  const { getAccessToken } = useAuth()

  /** Whether a delete request is in flight. */
  const [deleting, setDeleting] = useState<boolean>(false)
  /** Error message shown when the delete request fails. */
  const [deleteError, setDeleteError] = useState<string | null>(null)

  const isOpen: boolean = collection !== null

  // Clear any stale error each time the dialog opens for a (new) collection.
  useEffect(() => {
    if (collection !== null) {
      setDeleteError(null)
    }
  }, [collection])

  /**
   * Delete the collection on the backend, then notify the parent and close.
   * Failures are surfaced inline and leave the dialog open for a retry.
   *
   * @returns A promise that resolves once the delete attempt has completed.
   */
  const handleConfirmDelete = async (): Promise<void> => {
    if (collection === null || deleting) {
      return
    }
    setDeleting(true)
    setDeleteError(null)
    try {
      const token: string = await getAccessToken()
      await deleteCollection({ idToken: token, collectionId: collection.id })
      await onDeleted(collection.id)
      onClose()
    } catch (err) {
      const message: string =
        err instanceof Error ? err.message : "Failed to delete collection"
      setDeleteError(message)
    } finally {
      setDeleting(false)
    }
  }

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        // Prevent dismissing while a delete is in flight.
        if (!open && !deleting) {
          onClose()
        }
      }}
    >
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>Delete Collection</DialogTitle>
          <DialogDescription>
            {collection !== null
              ? `Delete "${collection.collectionName}" for everyone? This removes the collection from all its sources and cannot be undone.`
              : "Delete this collection? This cannot be undone."}
          </DialogDescription>
        </DialogHeader>

        {/* Inline error: shown only when a delete attempt failed. */}
        {deleteError !== null && (
          <p className="text-xs text-destructive" role="alert">
            {deleteError}
          </p>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={deleting}>
            Cancel
          </Button>
          <Button variant="destructive" onClick={handleConfirmDelete} disabled={deleting}>
            {deleting && <Loader2 className="size-3.5 animate-spin" />}
            {deleting ? "Deleting..." : "Delete collection"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/** The outcome of a successful rename, passed back to the parent. */
export interface RenameResult {
  /** The affected collection id. */
  collectionId: number
  /** The updated collection record returned by the backend. */
  updated: BiblioCollection
}

/** Props for the RenameCollectionDialog component. */
interface RenameCollectionDialogProps {
  /**
   * The collection to rename. When null, the dialog is closed; passing a
   * collection opens the dialog seeded with that collection's current name.
   */
  collection: BiblioCollection | null
  /** Called to request closing the dialog (e.g. Cancel, overlay, or Escape). */
  onClose: () => void
  /** Called after a successful rename with the updated collection record. */
  onRenamed: (result: RenameResult) => void | Promise<void>
}

/**
 * Modal dialog that overwrites a collection's display name.
 * 
 * @param props - Component props.
 * @param props.collection - The collection to rename, or null when closed.
 * @param props.onClose - Handler to close the dialog.
 * @param props.onRenamed - Handler invoked with the updated record on success.
 * @returns The rendered rename dialog.
 */
export function RenameCollectionDialog({
  collection,
  onClose,
  onRenamed,
}: RenameCollectionDialogProps): React.JSX.Element {
  const { getAccessToken } = useAuth()

  /** The current value of the name input. */
  const [name, setName] = useState<string>("")
  /** Whether a rename request is in flight. */
  const [saving, setSaving] = useState<boolean>(false)
  /** Error message shown when the rename request fails. */
  const [saveError, setSaveError] = useState<string | null>(null)

  const isOpen: boolean = collection !== null

  // Seed the input with the collection's current name and clear any stale
  // error each time the dialog opens for a (new) collection.
  useEffect(() => {
    if (collection !== null) {
      setName(collection.collectionName)
      setSaveError(null)
    }
  }, [collection])

  const trimmedName: string = name.trim()
  // Disable the save unless there is a non-empty name that actually differs
  // from the current one — renaming to the same value is a no-op.
  const canSave: boolean =
    collection !== null &&
    trimmedName.length > 0 &&
    trimmedName !== collection.collectionName &&
    !saving

  /**
   * Submit the new name to the backend, then notify the parent and close.
   * Failures (including a 403 for non-members) are surfaced inline and leave
   * the dialog open so the user can adjust and retry.
   *
   * @returns A promise that resolves once the rename attempt has completed.
   */
  const handleSave = async (): Promise<void> => {
    if (collection === null || !canSave) {
      return
    }

    setSaving(true)
    setSaveError(null)

    try {
      const token: string = await getAccessToken()
      const response = await updateCollectionName({
        idToken: token,
        collectionId: collection.id,
        collectionName: trimmedName,
      })

      // A rename never deletes the collection, so `item` is always present.
      // Fail loudly if it is somehow missing rather than silently swallowing it.
      if (response.item === undefined) {
        throw new Error("Rename succeeded but the server returned no collection record.")
      }

      await onRenamed({ collectionId: collection.id, updated: response.item })
      onClose()
    } catch (err) {
      const message: string =
        err instanceof Error ? err.message : "Failed to rename collection"
      setSaveError(message)
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        // Prevent dismissing (via overlay/escape) while a save is in flight so
        // the user cannot close the dialog mid-request.
        if (!open && !saving) {
          onClose()
        }
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Rename Collection</DialogTitle>
          <DialogDescription>
            {collection !== null
              ? `Give "${collection.collectionName}" a new name.`
              : "Give this collection a new name."}
          </DialogDescription>
        </DialogHeader>

        <Input
          autoFocus
          value={name}
          onChange={(event) => setName(event.target.value)}
          placeholder="Collection name"
          aria-label="Collection name"
          disabled={saving}
          onKeyDown={(event) => {
            if (event.key === "Enter" && canSave) {
              handleSave()
            }
          }}
        />

        {/* Inline error: shown only when a rename attempt failed. */}
        {saveError !== null && (
          <p className="text-xs text-destructive" role="alert">
            {saveError}
          </p>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={!canSave}>
            {saving && <Loader2 className="size-3.5 animate-spin" />}
            {saving ? "Saving..." : "Save name"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
